import utils from 'analytics/utils';
import locationUtils from 'utils/Location';
import anaConsts from 'analytics/constants';
import deepExtend from 'utils/deepExtend';
import anaUtils from 'analytics/utils';
import { getSuperChatReferrer } from 'ai/utils/sessionStorage';

export const getFulfillmentType = data => {
    const analyticsData = data?.analyticsData;

    if (analyticsData?.isPickup) {
        return anaConsts.FULFILLMENT_TYPE.Pickup;
    }

    if (analyticsData?.isSameDay) {
        return anaConsts.FULFILLMENT_TYPE.Sameday;
    }

    if (analyticsData?.isReplenishment) {
        return anaConsts.FULFILLMENT_TYPE.AutoReplenish;
    }

    return anaConsts.FULFILLMENT_TYPE.Standard;
};

export const updateProductStringsBase = (productString, fulfillmentType) => {
    let productStringsBase = productString || '';
    // Matcher: Extracts the fulfillment type value from analytics product strings that follow the format |eVar133=value|
    const EVAR133_MATCHER = /\|eVar133=([^|]+)/;
    const eVar133Match = productStringsBase.match(EVAR133_MATCHER);
    const currentEVar133Value = eVar133Match ? eVar133Match[1] : null;

    // If eVar133 doesn't exist or differs from fulfillmentType, set it with fulfillmentType
    if (!currentEVar133Value || currentEVar133Value !== fulfillmentType) {
        if (currentEVar133Value) {
            // Replace existing eVar133 value
            productStringsBase = productStringsBase.replace(/\|eVar133=[^|]+/, `|eVar133=${fulfillmentType}`);
        } else {
            // Add eVar133 if it doesn't exist
            productStringsBase = `${productStringsBase}|eVar133=${fulfillmentType}`;
        }
    }

    return productStringsBase;
};

/* eslint-disable-next-line complexity */
export default function (data) {
    const currentEvent = utils.getMostRecentEvent(anaConsts.LINK_TRACKING_EVENT);
    const isHomepageRewardCarousel = locationUtils.isHomepage() && data?.analyticsData?.containerTitle === anaConsts.CAROUSEL_NAMES.REWARD_BAZAAR;
    const rootContainerName = (data.sku.rootContainerName || data.analyticsData.rootContainerName)?.toLowerCase();
    const isNotUseItWith = rootContainerName?.toLowerCase() !== anaConsts.CONTEXT.USE_IT_WITH;

    let linkName = `Add To Basket${data.analyticsData.isPickup ? ' For Store Pickup' : data.analyticsData.isSameDay ? ' For Same Day Delivery' : ''}`;
    linkName = data.analyticsData.isSameDay ? linkName.toLowerCase() : linkName;
    linkName = data.isGalleryLightBox ? `${anaConsts.PAGE_TYPES.GALLERY}:${anaConsts.LinkData.GALLERY_ADD_TO_BASKET}` : linkName;
    let actionInfoData =
        rootContainerName && !data.analyticsData.isBIRBPageRewardModal && isNotUseItWith
            ? data.context !== anaConsts.CONTEXT.QUICK_LOOK
                ? `${rootContainerName}:add to basket`
                : 'add to basket'
            : linkName;

    const isCustomSet = data?.sku?.configurableOptions && isNotUseItWith;
    actionInfoData = isCustomSet ? anaConsts.LinkData.ADD_TO_BASKET_CUSTOM_SET : actionInfoData;
    actionInfoData = data.analyticsData.isGiftCardQuickAdd
        ? `${anaConsts.LinkData.GIFT_CARD}:${anaConsts.ACTION_INFO.ADD_TO_BASKET}`
        : actionInfoData;
    actionInfoData = data.isGalleryLightBox ? `${anaConsts.PAGE_TYPES.GALLERY}:${anaConsts.LinkData.GALLERY_ADD_TO_BASKET}` : actionInfoData;
    const isLovesModal = data.context === anaConsts.PAGE_TYPES.LOVES_MODAL;
    const isMyListsModal = data.context === anaConsts.PAGE_NAMES.MY_LISTS;
    const fromChooseOptionsModal = data?.analyticsData?.basket?.fromChooseOptionsModal;
    const fromSuperChatModal = data?.analyticsData?.fromSuperChatModal;
    const isSYSPage = locationUtils.isShopMyStorePage() || locationUtils.isShopSameDayPage();
    const fromBasketYourSaved = data.context === 'basketcheckout-yourloves';
    const isBasketPage = locationUtils.isBasketPage();
    const eventStrings = [anaConsts.Event.SC_ADD];
    const productStrings = data?.analyticsData?.productStrings?.[0];

    if (data?.isGalleryLightBox) {
        eventStrings.push(anaConsts.Event.GALLERY_COMPONENT_INTERACTION);
    }

    const fulfillmentType = getFulfillmentType(data);
    const productStringsBase = updateProductStringsBase(productStrings, fulfillmentType);

    const attributes = {
        linkName: data.analyticsData.isAddFullSize ? 'Add Full Size' : isCustomSet ? anaConsts.LinkData.ADD_TO_BASKET_CUSTOM_SET : linkName,
        actionInfo: actionInfoData,
        eventStrings,
        biRewardType: data.sku.biType,
        skuId: data.sku.skuId,
        productStrings: productStringsBase,
        pageType: digitalData.page.category.pageType
    };

    if (isHomepageRewardCarousel) {
        attributes.previousPage = `${anaConsts.PAGE_TYPES.HOMEPAGE}:${anaConsts.PAGE_NAMES.HOMEPAGE}:n/a:*`;
    }

    if (data.analyticsData.origin) {
        attributes.actionInfo = `${data.analyticsData.origin}:add to basket`;
    }

    if (data.previousPage && data.previousPage.includes('basket')) {
        attributes.actionInfo = 'add to basket';
    }

    if (data.context === anaConsts.CONTEXT.FREQUENTLY_BOUGHT_TOGETHER || data.context === anaConsts.CONTEXT.CLEAN_AT_SEPHORA) {
        attributes.eventStrings = [anaConsts.Event.SC_ADD];
    }

    if (data.context === anaConsts.CONTEXT.FREQUENTLY_BOUGHT_TOGETHER && data.analyticsData.isMultiProductsAdd) {
        const multiProductStrings =
            Array.isArray(data.analyticsData.sku) && data.analyticsData.sku.map(item => `;${item.skuId};;;;eVar26=${item.skuId}`).join(',');

        attributes.actionInfo = `${anaConsts.CONTEXT.FREQUENTLY_BOUGHT_TOGETHER}:add all to basket`;
        attributes.productStrings = multiProductStrings;
        attributes.eventStrings = [anaConsts.Event.SC_ADD];
    }

    /* ILLUPH-119839
     * According to JIRA and business, requirement is to have prop55 populate with
     * actionCampaignString listed below only for the products within the
     * loves, purchases, and services carousels on the profile lists page.
     * On other pages we will continue to populate eVar75 with actionCampaignString.
     */
    if (rootContainerName) {
        const productId = data.sku.productId || data.analyticsData.productId;

        if (
            rootContainerName === anaConsts.CAROUSEL_NAMES.LOVES ||
            rootContainerName === anaConsts.CAROUSEL_NAMES.PURCHASES ||
            rootContainerName === anaConsts.CAROUSEL_NAMES.SERVICES ||
            rootContainerName === anaConsts.CAROUSEL_NAMES.USER_PROFILE
        ) {
            if (data.analyticsData.isAddFullSize) {
                attributes.actionInfo = `${rootContainerName}:${productId}:add full size`;
            } else {
                attributes.actionInfo = `${rootContainerName}:Add To Basket`;
            }
        } else if (data.context === anaConsts.CONTEXT.FREQUENTLY_BOUGHT_TOGETHER && data.analyticsData.isMultiProductsAdd) {
            const skuIds = Array.isArray(data.analyticsData.sku) && data.analyticsData.sku.map(item => item.productId);
            attributes.internalCampaign = `${anaConsts.CONTEXT.FREQUENTLY_BOUGHT_TOGETHER}:${skuIds.join('-')}:add-all-to-basket`;
        } else {
            attributes.internalCampaign = `${rootContainerName}:${productId}:add-to-basket`;
            const pageSourceName = anaUtils.getCustomPageSourceName();

            if (pageSourceName) {
                attributes.internalCampaign = `${pageSourceName}:${attributes.internalCampaign}`;
            }
        }
    } else if (data.isGalleryLightBox) {
        attributes.internalCampaign = `${anaConsts.PAGE_TYPES.GALLERY}:${anaConsts.CONTEXT.QUICK_LOOK}:${anaConsts.LinkData.GALLERY_ADD_TO_BASKET}`;
        attributes.previousPage = `${anaConsts.PAGE_TYPES.COMMUNITY}:${anaConsts.PAGE_NAMES.GALLERY_LIGHTBOX}:n/a:*`;
        attributes.pageDetail = anaConsts.PAGE_NAMES.COMMUNITY_PRODUCT_MODAL;
    }

    // UTS-1071 Fix for pageDetail
    if (digitalData.page?.attributes?.sephoraPageInfo?.pageName && typeof data.pageDetail === 'undefined') {
        attributes.pageDetail = digitalData.page.attributes.sephoraPageInfo.pageName?.split(':')?.[1];
    }

    if (isMyListsModal) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.MY_LISTS}`;

        if (locationUtils.isMyCustomListPage()) {
            const actionInfo = anaConsts.ACTION_INFO.ADD_TO_THE_BASKET;
            attributes.actionInfo = actionInfo;
            attributes.pageName = anaConsts.EVENT_NAMES.LOVES.LIST_DETAIL_PAGE_NAME;
            attributes.eventStrings = [anaConsts.Event.SC_ADD];
            const pageDetail = anaConsts.EVENT_NAMES.LOVES.LIST_DETAIL_PAGE_NAME;
            digitalData.page.pageInfo.pageName = pageDetail;
            digitalData.page.attributes.sephoraPageInfo.pageName = pageDetail;
        }
    } else if (isLovesModal) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.LOVES_FLYOUT}`;
    } else if (fromChooseOptionsModal) {
        const shouldRemoveQtyFromProductStrings = isSYSPage || isBasketPage;

        // GUAR-6954
        if (shouldRemoveQtyFromProductStrings && !!attributes.productStrings) {
            const QTY_POSITION = 2;
            const parts = attributes.productStrings.split(';');

            if (parts[QTY_POSITION]) {
                parts[QTY_POSITION] = '';
                attributes.productStrings = parts.join(';');
            }
        }

        const isFromCarousel = !!data?.analyticsData?.rootContainerName;
        const carouselName = (data?.analyticsData?.rootContainerName || 'n/a').toLowerCase();
        const PRICE_POSITION = 4;
        const uniquePlacement = data.context;
        const skuPrice = anaUtils.removeCurrencySymbol(data?.sku?.salePrice || data?.sku?.listPrice);
        const productStringsWithPrice = utils.insertValueAtPosition(attributes.productStrings, skuPrice, PRICE_POSITION);
        attributes.actionInfo = `${anaConsts.ACTION_INFO.ADD_TO_BASKET}:${uniquePlacement}`;

        if (data.context === anaConsts.CONTEXT.MY_LISTS_HOMEPAGE) {
            attributes.productStrings = `${productStringsWithPrice}|eVar72=${anaConsts.PAGE_NAMES.MY_LISTS}`;
            attributes.actionInfo = `${anaConsts.ACTION_INFO.ADD_TO_BASKET}:${uniquePlacement}`;
            let pageDetail = anaConsts.EVENT_NAMES.LOVES.LIST_HOME;

            if (locationUtils.isMyCustomListPage()) {
                pageDetail = anaConsts.EVENT_NAMES.LOVES.LIST_DETAIL_PAGE_NAME;
            }

            if (isFromCarousel) {
                attributes.productStrings = `${attributes.productStrings}|eVar69=${carouselName}`;
                attributes.eventStrings = [anaConsts.Event.SC_ADD, anaConsts.Event.EVENT_269];
            }

            attributes.pageName = pageDetail;
            digitalData.page.pageInfo.pageName = pageDetail;
            digitalData.page.attributes.sephoraPageInfo.pageName = pageDetail;
        } else if (data.context === anaConsts.CONTEXT.MY_LISTS_FLYOUT) {
            attributes.productStrings = `${productStringsWithPrice}|eVar72=${anaConsts.PAGE_TYPES.MY_LISTS_FLYOUT}`;
            attributes.actionInfo = `${anaConsts.ACTION_INFO.ADD_TO_BASKET}:${uniquePlacement}`;
            const pageDetail = anaConsts.EVENT_NAMES.LOVES.LIST_FLY_OUT_PAGE_NAME;

            if (isFromCarousel) {
                attributes.productStrings = `${attributes.productStrings}|eVar69=${carouselName}`;
                attributes.eventStrings = [anaConsts.Event.SC_ADD, anaConsts.Event.EVENT_269];
            }

            attributes.pageName = pageDetail;
            digitalData.page.pageInfo.pageName = pageDetail;
            digitalData.page.attributes.sephoraPageInfo.pageName = pageDetail;
        } else if (data.context === anaConsts.CONTEXT.LOVES_CAROUSEL) {
            const eVar72Value = anaConsts.PAGE_TYPES.MY_LISTS;
            const pageDetail = anaConsts.EVENT_NAMES.LOVES.LIST_FLY_OUT_PAGE_NAME;
            digitalData.page.pageInfo.pageName = pageDetail;
            digitalData.page.attributes.sephoraPageInfo.pageName = pageDetail;

            attributes.productStrings = `${productStringsWithPrice}|eVar69=${carouselName}|eVar72=${eVar72Value}`;
            attributes.eventStrings = [anaConsts.Event.SC_ADD, anaConsts.Event.EVENT_269];
            attributes.pageName = pageDetail;
            digitalData.page.pageInfo.pageName = pageDetail;
            digitalData.page.attributes.sephoraPageInfo.pageName = pageDetail;
        } else if (isSYSPage || isBasketPage) {
            const pageSourceName = anaUtils.getCustomPageSourceName();
            const evar72 = isBasketPage && fromBasketYourSaved ? anaConsts.PAGE_NAMES.BASKET : pageSourceName || 'carousel';

            attributes.eventStrings = [anaConsts.Event.SC_ADD, anaConsts.Event.EVENT_269];
            attributes.actionInfo = `${carouselName}:add to basket`;
            attributes.productStrings = `${attributes.productStrings}|eVar69=${carouselName}|eVar72=${evar72}`;
            attributes.internalCampaign = `${carouselName}:add-to-basket`;

            if (pageSourceName) {
                attributes.internalCampaign = `${pageSourceName}:${attributes.internalCampaign}`;
            }
        } else if (data.context === anaConsts.CONTEXT.BUY_IT_AGAIN) {
            attributes.actionInfo = `${anaConsts.ACTION_INFO.ADD_TO_BASKET}:${uniquePlacement}`;
        } else if (
            uniquePlacement === anaConsts.CHOOSE_OPTIONS_PLACEMENT_NAMES.PDP_YOU_MAY_ALSO_LIKE ||
            uniquePlacement === anaConsts.CHOOSE_OPTIONS_PLACEMENT_NAMES.HOMEPAGE_CHOSEN_FOR_YOU ||
            uniquePlacement === anaConsts.CHOOSE_OPTIONS_PLACEMENT_NAMES.HOMEPAGE_NEW_ARRIVALS ||
            uniquePlacement === anaConsts.CHOOSE_OPTIONS_PLACEMENT_NAMES.HOMEPAGE_SELLING_FAST
        ) {
            // Constructor carousel (YMAL, RFY, etc.) from Choose Options Modal
            attributes.eventStrings = [anaConsts.Event.SC_ADD, anaConsts.Event.EVENT_269];
            attributes.actionInfo = `${anaConsts.ACTION_INFO.ADD_TO_BASKET}:${uniquePlacement}`;
            attributes.productStrings = `${productStringsWithPrice}|eVar69=${carouselName}|eVar72=${uniquePlacement}`;
            attributes.internalCampaign = `${carouselName}:add-to-basket`;
        } else {
            attributes.productStrings = `${productStringsWithPrice}|eVar72=${uniquePlacement}`;
        }
    } else if (fromSuperChatModal) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.SUPER_CHAT.SUPERCHAT_EVAR}`;
    } else if (locationUtils.isRwdCreditCardPage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.CREDIT_CARD}`;
    } else if (locationUtils.isBIPage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.BEAUTY_INSIDER}`;
    } else if (locationUtils.isBasketPage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.BASKET}`;
    } else if (locationUtils.isBIRBPage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.REWARD_BAZAAR}`;
        attributes.eventStrings = [anaConsts.Event.SC_ADD, anaConsts.Event.EVENT_71];
        attributes.actionInfo = `${anaConsts.CAROUSEL_NAMES.REWARD_BAZAAR}:${anaConsts.CAMPAIGN_STRINGS.ADD_TO_BASKET}`;
    } else if (locationUtils.isAutoreplenishPage()) {
        const carouselName = (data?.analyticsData?.rootContainerName || 'n/a').toLowerCase();
        const productId = data.sku.productId || data.analyticsData.productId;
        attributes.actionInfo = `${carouselName}:${anaConsts.ACTION_INFO.ADD_TO_BASKET}`;
        attributes.productStrings = `${attributes.productStrings}|eVar72=${carouselName}`;
        attributes.internalCampaign = `${carouselName}:${productId}:${anaConsts.CAMPAIGN_STRINGS.ADD_TO_BASKET}`;
    } else if (locationUtils.isSearchPage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.SEARCH}`;
    } else if (locationUtils.isLovesListPage() || locationUtils.isListsPage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.LOVE_LIST}`;
    } else if (locationUtils.isPurchaseHistoryPage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.BUY_IT_AGAIN}`;
    } else if (locationUtils.isNthCategoryPage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.CATEGORY}`;
    } else if (locationUtils.isOrderDetailsPage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.ORDER_DETAIL}`;
    } else if (locationUtils.isOrdersPage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.RESTOCK_PAST_PURCHASES}`;
    } else if (locationUtils.isCommunityProfilePage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.COMMUNITY}`;
    } else if (locationUtils.isHomepage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.HOMEPAGE}`;
    } else if (locationUtils.isProductPage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.PRODUCT}`;
    } else if (locationUtils.isCreatorStoreFrontPage()) {
        attributes.productStrings = `${attributes.productStrings}|eVar72=${anaConsts.PAGE_NAMES.CSF}`;
    } else if (isSYSPage) {
        const carouselName = (data?.analyticsData?.rootContainerName || 'n/a').toLowerCase();
        attributes.eventStrings = [anaConsts.Event.SC_ADD, anaConsts.Event.EVENT_269];
        attributes.actionInfo = `${carouselName}:add to basket`;
        attributes.productStrings = `${attributes.productStrings}|eVar69=${carouselName}|eVar72=carousel`;
    }

    // Add sotV425 for "Add to basket From Product page
    if (locationUtils.isProductPage() && !fromSuperChatModal) {
        // Try digitalData first (set during page load)
        let superChatReferrer = digitalData?.page?.attributes?.sotV425;

        if (!superChatReferrer) {
            superChatReferrer = getSuperChatReferrer();
        }

        if (superChatReferrer) {
            data.analyticsData.sotV425 = superChatReferrer;
            // add to basket need sotV425 in data.analyticsData.sotV425
        }
    }

    deepExtend(currentEvent, { eventInfo: { attributes } });
}
